'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSidebarSearch, highlightMatches } from '@/src/lib/navigation-search';
import type { ResolvedNavigationSection } from '@/src/lib/navigation';

export interface SidebarSearchProps {
  onSelect?: () => void;
  sections?: readonly ResolvedNavigationSection[];
}

export function SidebarSearch({ onSelect, sections }: SidebarSearchProps) {
  const { query, setQuery, results } = useSidebarSearch(sections);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, []);

  function handleSelect(href: string) {
    router.push(href);
    setOpen(false);
    setQuery('');
    onSelect?.();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) handleSelect(r.href);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={ref} className="relative px-3 pb-2">
      <div className="relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--k-color-text-muted)]"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Sidebar durchsuchen…"
          aria-label="Sidebar-Suche"
          className="w-full rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] py-1.5 pl-8 pr-2 text-xs text-[var(--k-color-text-primary)] placeholder:text-[var(--k-color-text-muted)] focus:border-[var(--k-color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--k-color-accent)]/20"
        />
      </div>
      {open && query.length >= 2 ? (
        <div
          role="listbox"
          className="absolute left-3 right-3 z-50 mt-1 max-h-80 overflow-y-auto rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]"
        >
          {results.length === 0 ? (
            <div className="p-3 text-center text-xs text-[var(--k-color-text-muted)]">
              Keine Treffer für „{query}“
            </div>
          ) : (
            <ul className="py-1">
              {results.map((result, idx) => {
                const labelSegments = highlightMatches(result.label, query);
                const sectionSegments = highlightMatches(result.section, query);
                return (
                  <li key={result.href} role="option" aria-selected={idx === activeIndex}>
                    <button
                      type="button"
                      onClick={() => handleSelect(result.href)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left transition ${
                        idx === activeIndex
                          ? 'bg-[var(--k-color-accent-soft)] text-[var(--k-color-text-link)]'
                          : 'text-[var(--k-color-text-primary)]'
                      }`}
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[var(--k-color-surface-muted)] text-[10px] font-bold uppercase tracking-wide text-[var(--k-color-text-muted)]">
                        {result.section.charAt(0)}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-semibold">
                          {labelSegments.map((seg, i) => (
                            <span key={i} className={seg.match ? 'font-bold underline' : ''}>
                              {seg.text}
                            </span>
                          ))}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-[var(--k-color-text-muted)]">
                          {sectionSegments.map((seg, i) => (
                            <span key={i} className={seg.match ? 'font-bold text-[var(--k-color-text-link)]' : ''}>
                              {seg.text}
                            </span>
                          ))}{' '}
                          · {result.group}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-1.5 text-[10px] text-[var(--k-color-text-muted)]">
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">↑↓</kbd> navigieren ·{' '}
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">Enter</kbd> öffnen ·{' '}
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">Esc</kbd> schließen
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SidebarSearchButton() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-label="Sidebar-Suche umschalten"
        title="Sidebar-Suche (Shift+/)"
        className="flex h-10 w-full items-center gap-2 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-3 text-xs font-medium text-[var(--k-color-text-muted)] shadow-[var(--k-shadow-xs)] transition hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span className="flex-1 text-left">Sidebar durchsuchen…</span>
        <kbd className="rounded bg-[var(--k-color-surface-muted)] px-1.5 py-0.5 font-mono text-[10px]">/</kbd>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Sidebar-Suche"
          className="fixed inset-y-0 left-0 z-50 flex w-[20rem] flex-col border-r border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-[var(--k-color-border-subtle)] px-4 py-3">
            <h2 className="text-sm font-semibold">Sidebar-Suche</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Schließen"
              className="rounded-md p-1 text-[var(--k-color-text-muted)] hover:bg-[var(--k-color-surface-muted)]"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <SidebarSearchInline onSelect={() => setOpen(false)} />
        </div>
      ) : null}
    </>
  );
}

function SidebarSearchInline({ onSelect }: { onSelect?: () => void }) {
  return (
    <div className="flex-1 overflow-y-auto p-2">
      <SidebarSearch onSelect={onSelect} />
    </div>
  );
}

export function SidebarSearchTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Sidebar durchsuchen"
        title="Suche (Shift+/)"
        className="flex h-8 w-full items-center gap-2 rounded-[var(--k-radius-sm)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2 text-[11px] font-medium text-[var(--k-color-text-muted)] transition hover:border-[var(--k-color-border-default)] hover:text-[var(--k-color-text-primary)]"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span className="flex-1 text-left">Suchen</span>
        <kbd className="rounded bg-[var(--k-color-surface-muted)] px-1 font-mono text-[9px]">/</kbd>
      </button>
      {open ? <SidebarSearchDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function SidebarSearchDialog({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/55 px-4 pt-[15vh]">
      <div className="w-full max-w-lg overflow-hidden rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <div className="flex items-center justify-between border-b border-[var(--k-color-border-subtle)] px-4 py-2">
          <h3 className="text-sm font-semibold">Schnellzugriff suchen</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-[var(--k-color-text-muted)] hover:bg-[var(--k-color-surface-muted)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <SidebarSearch onSelect={onClose} />
      </div>
    </div>
  );
}

export function SidebarSearchLink({ children, href, onClick }: { children: React.ReactNode; href: string; onClick?: () => void }) {
  return (
    <Link href={href} onClick={onClick} className="block">
      {children}
    </Link>
  );
}
